/* YG Stats — scroll-pinned progressive reveal. See sections/
   yg-home-stats.liquid's own header comment for the structural rationale
   (tall outer wrapper + sticky inner pin). This file computes scroll
   progress through that tall wrapper, gives each line an equal-sized
   slice of it, and sets that line's own --yg-reveal (0-1) custom property
   every scroll event — yg-home-stats.css turns that into a left-to-right
   clip-path wipe. Continuous per-line reveal, not a binary show/hide:
   explicit request ("the text itself should load bit by bit on scroll,
   not all at once") — a line is PARTIALLY wiped in while scrolling
   through its own slice, not fully hidden until a threshold then fully
   shown. Earlier version toggled a single .is-revealed class per line
   instead; this replaces that entirely.

   Progressive enhancement: .yg-stats--js (which is what actually clips
   lines pre-reveal, see yg-home-stats.css) is only added once this class
   successfully initialises — a no-JS visitor, or a JS error before that
   point, sees every line already fully visible instead of permanently
   clipped away. */
class YgStats extends HTMLElement {
  connectedCallback() {
    this.lines = Array.from(this.querySelectorAll('[data-yg-stats-line]'));
    if (!this.lines.length) return;

    this.dots = Array.from(this.querySelectorAll('[data-yg-stats-dot]'));
    this.hint = this.querySelector('[data-yg-stats-hint]');

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.reduceMotion) {
      // Every line is already visible here (no .yg-stats--js, so nothing is
      // clipped), which makes a progress indicator a lie: it would show one
      // step of a sequence that is not sequencing. Remove it rather than
      // leave it stuck on the first dot.
      const progress = this.querySelector('[data-yg-stats-progress]');
      if (progress) progress.remove();
      return;
    }

    this.classList.add('yg-stats--js');
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.onScroll();
  }

  onScroll() {
    const rect = this.getBoundingClientRect();
    const scrollable = rect.height - window.innerHeight;
    if (scrollable <= 0) {
      // Nothing to scroll through, so every line shows at once. Same reason as
      // reduced motion: an indicator for a sequence that is not happening
      // would mislead, so light every dot rather than pretend to a position.
      this.lines.forEach((line) => line.style.setProperty('--yg-reveal', 1));
      this.dots.forEach((dot) => {
        dot.classList.remove('is-on');
        dot.classList.add('is-past');
      });
      if (this.hint) this.hint.classList.add('is-gone');
      return;
    }
    const scrolled = Math.min(Math.max(-rect.top, 0), scrollable);
    const progress = scrolled / scrollable;
    // Each line owns an equal 1/count slice of the total scroll range —
    // the same slice size the section's own height is built from (see
    // --yg-stats-count in yg-home-stats.css). Within its own slice a
    // line's local reveal runs 0 -> 1; before its slice it's 0 (not yet
    // started), after its slice it stays 1 (fully revealed, accumulating
    // rather than un-revealing on further scroll).
    // A line is either shown whole or not at all. The reveal used to track
    // scroll continuously across each line's slice, which meant that wherever
    // the reader stopped, one line was frozen part-way through its wipe and
    // read as a sentence cut in half rather than as an animation in progress.
    //
    // Each line still owns its own slice; it now flips to 1 once the slice is
    // far enough along to read as committed, and back to 0 on the way up, so
    // the sequence still reveals line by line with scroll and still reverses.
    const slice = 1 / this.lines.length;
    const TRIGGER = 0.35; // fraction into a line's slice at which it commits
    let current = -1;
    this.lines.forEach((line, i) => {
      const local = (progress - i * slice) / slice;
      const on = local >= TRIGGER;
      line.style.setProperty('--yg-reveal', on ? 1 : 0);
      if (on) current = i;
    });

    // Dots mirror the same commit points the lines use, so the indicator can
    // never disagree with what is actually on screen.
    if (this.dots.length) {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-on', i === current);
        dot.classList.toggle('is-past', i < current);
      });
    }

    // The hint asks the reader to scroll; once they have, it has nothing left
    // to say. Tied to the first line committing rather than to any scroll at
    // all, so a nudge that reveals nothing does not dismiss it prematurely.
    if (this.hint) this.hint.classList.toggle('is-gone', current >= 0);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }
}
customElements.define('yg-stats', YgStats);
